import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ExternalLink, Github, ChevronRight, Sparkles, Code2, Smartphone, Globe, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSimpleScrollReveal } from '@/hooks/useSimpleScrollReveal';
import { BlurText } from '@/components/ui/animated-blur-text';
import { getProjects, getCategories, getProjectsByCategory } from '@/utils/portfolio';
import { Project } from '@/types/portfolio';
import PortfolioModal from '../PortfolioModal';

const ModernPortfolioSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useSimpleScrollReveal({ once: true });
  const projectsRef = useSimpleScrollReveal({ once: true, delay: 200 });
  
  const categories = getCategories();
  const projects = getProjectsByCategory(selectedCategory);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'web': return <Globe className="w-4 h-4" />;
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'solution': return <Code2 className="w-4 h-4" />;
      case 'ai': return <Brain className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  return (
    <section id="portfolio" className="py-32 px-4 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/20 via-transparent to-muted/20" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          ref={sectionRef as any}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 backdrop-blur-sm border border-border mb-6"
          >
            <span className="text-sm font-medium text-muted-foreground">
              포트폴리오
            </span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <BlurText 
              text="성공적으로 완료한 "
              className="text-4xl md:text-5xl font-bold inline"
              duration={0.8}
              as="span"
            />
            <span className="text-primary">프로젝트</span>
          </h2>
          <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
            다양한 산업 분야에서 검증된 기술력과 창의적인 솔루션
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {Object.entries(categories).map(([id, label]) => (
            <motion.button
              key={id}
              onClick={() => setSelectedCategory(id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all",
                selectedCategory === id
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "bg-muted/50 hover:bg-muted text-foreground/70 hover:text-foreground"
              )}
            >
              {getCategoryIcon(id)}
              {label}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          ref={projectsRef as any}
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {projects.map((project) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => handleProjectClick(project)}
                className="group relative bg-zinc-950 rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-700 transition-all duration-300 cursor-pointer"
              >
                {/* Featured badge */}
                {project.featured && (
                  <div className="absolute top-4 left-4 z-10">
                    <div className="px-3 py-1 bg-yellow-400/10 border border-yellow-400/20 rounded-full">
                      <span className="text-xs font-medium text-yellow-400">Featured</span>
                    </div>
                  </div>
                )}

                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden bg-zinc-900">
                  {project.thumbnail ? (
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  {/* Fallback icon */}
                  <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800/50 to-zinc-900 ${project.thumbnail ? 'hidden' : ''}`}>
                    <div className="text-zinc-700">
                      {getCategoryIcon(project.category)}
                    </div>
                  </div>

                  {/* Hover overlay */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: hoveredProject === project.id ? 1 : 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center"
                  >
                    <div className="text-white text-sm font-medium">상세보기</div>
                  </motion.div>
                </div>

                <div className="p-6">
                  {/* Header */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-zinc-500">
                        {categories[project.category]}
                      </span>
                      <span className="text-xs text-zinc-600">•</span>
                      <span className="text-xs text-zinc-500">{project.period}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {project.title}
                    </h3>
                    <p className="text-sm text-zinc-400 line-clamp-2">
                      {project.description}
                    </p>
                  </div>

                  {/* Metrics */}
                  {project.metrics && Object.keys(project.metrics).length > 0 && (
                    <div className="mb-4 space-y-2">
                      {Object.entries(project.metrics).slice(0, 2).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-zinc-500 capitalize">{key}:</span>
                          <span className="text-zinc-300 font-medium">
                            {Array.isArray(value) ? value.join(', ') : value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 text-xs bg-zinc-800/50 text-zinc-400 rounded-md border border-zinc-700/50"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.tags.length > 3 && (
                      <span className="px-2 py-1 text-xs text-zinc-500">
                        +{project.tags.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-muted-foreground mb-6">
            더 많은 프로젝트가 궁금하신가요?
          </p>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:shadow-lg transition-all"
          >
            프로젝트 문의하기
            <ChevronRight className="w-4 h-4" />
          </motion.a>
        </motion.div>
      </div>
      
      {/* Portfolio Modal */}
      <PortfolioModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
      />
    </section>
  );
};

export default ModernPortfolioSection;